import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { formatEUR } from "../lib/api";
import { toast } from "sonner";
import BlogEditor from "../components/BlogEditor";
import { Plus, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

export default function AdminPage() {
  const { user, loading, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [emails, setEmails] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} for new | post object for edit

  const load = async () => {
    try {
      const [s, p, o, n, e, b, m] = await Promise.all([
        axios.get(`${API}/admin/stats`, cfg),
        axios.get(`${API}/admin/products`, cfg),
        axios.get(`${API}/admin/orders`, cfg),
        axios.get(`${API}/admin/newsletter`, cfg),
        axios.get(`${API}/admin/emails`, cfg),
        axios.get(`${API}/admin/blog`, cfg),
        axios.get(`${API}/admin/contact`, cfg),
      ]);
      setStats(s.data); setProducts(p.data); setOrders(o.data); setSubs(n.data); setEmails(e.data); setBlogPosts(b.data); setMessages(m.data);
    } catch (err) { console.error(err); }
  };

  const toggleHandled = async (id, handled) => {
    try {
      await axios.patch(`${API}/admin/contact/${id}?handled=${handled}`, {}, cfg);
      setMessages((prev) => prev.map((m) => m.id === id ? { ...m, handled } : m));
      toast.success(handled ? "Marcado como respondido" : "Marcado como pendiente");
    } catch { toast.error("Error al actualizar"); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return <div className="p-16 text-center text-[color:var(--brand-neutral)]">Cargando…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const updateProduct = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/products/${id}`, patch, cfg);
      toast.success("Producto actualizado");
      load();
    } catch { toast.error("Error al actualizar"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="eyebrow">Panel admin</p>
          <h1 className="font-serif text-4xl text-[color:var(--brand-primary)] font-light">Mimes Gourmet</h1>
        </div>
        <Button data-testid="admin-logout" variant="outline" onClick={logout}>Salir</Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            ["Productos", stats.products],
            ["Pedidos pagados", stats.paid_orders],
            ["Ingresos", formatEUR(stats.revenue_eur)],
            ["Newsletter", stats.newsletter_subscribers],
          ].map(([k, v]) => (
            <div key={k} className="bg-white border border-[color:var(--brand-border)] rounded-md p-5">
              <p className="eyebrow">{k}</p>
              <p className="font-serif text-3xl mt-2 text-[color:var(--brand-primary)]">{v}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="products">
        <TabsList className="bg-[color:var(--brand-bg-alt)]">
          <TabsTrigger value="products" data-testid="tab-products">Productos</TabsTrigger>
          <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Pedidos</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">
            Mensajes {messages.filter(m => !m.handled).length > 0 && <span className="ml-1 bg-[color:var(--brand-terracotta)] text-white text-[9px] rounded-full px-1.5 py-0.5">{messages.filter(m => !m.handled).length}</span>}
          </TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="tab-newsletter">Newsletter</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">Emails</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6 space-y-3">
          {products.map((p) => (
            <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-white border border-[color:var(--brand-border)] rounded-md p-4 flex items-center gap-4">
              <img src={p.image} alt={p.name} className="w-14 h-14 object-cover rounded-sm" />
              <div className="flex-1">
                <p className="font-serif text-lg">{p.name}</p>
                <p className="text-xs text-[color:var(--brand-neutral)]/60">{p.slug}</p>
              </div>
              <label className="text-xs flex flex-col">
                Precio (€)
                <Input type="number" step="0.5" defaultValue={p.price_eur} className="w-24 mt-1"
                  onBlur={(e) => { const v = parseFloat(e.target.value); if (v && v !== p.price_eur) updateProduct(p.id, { price_eur: v }); }} />
              </label>
              <label className="text-xs flex flex-col">
                Stock
                <Input type="number" defaultValue={p.stock} className="w-20 mt-1"
                  onBlur={(e) => { const v = parseInt(e.target.value); if (Number.isFinite(v) && v !== p.stock) updateProduct(p.id, { stock: v }); }} />
              </label>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="blog" className="mt-6 space-y-4">
          {editing !== null ? (
            <BlogEditor
              initial={editing}
              onSave={(saved) => {
                setEditing(null);
                setBlogPosts((prev) => {
                  const idx = prev.findIndex((p) => p.id === saved.id);
                  if (idx === -1) return [saved, ...prev];
                  const next = [...prev];
                  next[idx] = saved;
                  return next;
                });
              }}
              onCancel={() => setEditing(null)}
              onDelete={(id) => {
                setEditing(null);
                setBlogPosts((prev) => prev.filter((p) => p.id !== id));
              }}
            />
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-[color:var(--brand-neutral)]">{blogPosts.length} artículos · publica y edita sin código</p>
                <Button
                  data-testid="blog-new-btn"
                  onClick={() => setEditing({})}
                  className="bg-[color:var(--brand-primary)] text-[color:var(--brand-bg)] hover:opacity-90"
                >
                  <Plus className="w-4 h-4 mr-2" /> Nuevo artículo
                </Button>
              </div>
              <div className="space-y-3">
                {blogPosts.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`blog-row-${p.slug}`}
                    className="bg-white border border-[color:var(--brand-border)] rounded-md p-4 flex items-center gap-4"
                  >
                    <img src={p.image} alt={p.title} className="w-16 h-16 object-cover rounded-sm shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg truncate">{p.title}</p>
                      <p className="text-xs text-[color:var(--brand-neutral)]/60 truncate">/{p.slug} · {p.published_at}</p>
                    </div>
                    <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded-sm ${p.published !== false ? "bg-[color:var(--brand-sage)]/20 text-[color:var(--brand-sage)]" : "bg-[color:var(--brand-bg-alt)] text-[color:var(--brand-neutral)]"}`}>
                      {p.published !== false ? "Publicado" : "Borrador"}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => setEditing(p)} data-testid={`blog-edit-${p.slug}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                    </Button>
                  </div>
                ))}
                {blogPosts.length === 0 && <p className="text-sm text-[color:var(--brand-neutral)]">Aún no hay artículos. ¡Crea el primero!</p>}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 ? <p className="text-sm text-[color:var(--brand-neutral)]">Aún no hay pedidos.</p> :
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.session_id} className="bg-white border border-[color:var(--brand-border)] rounded-md p-3 flex justify-between text-sm">
                <span className="font-mono text-xs">{o.session_id?.slice(-14)}</span>
                <span>{o.items?.length || 0} items</span>
                <span>{formatEUR(o.amount || 0)}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${o.payment_status === "paid" ? "bg-[color:var(--brand-sage)]/20 text-[color:var(--brand-sage)]" : "bg-[color:var(--brand-bg-alt)]"}`}>
                  {o.payment_status}
                </span>
              </div>
            ))}
          </div>}
        </TabsContent>

        <TabsContent value="messages" className="mt-6 space-y-3">
          {messages.length === 0 ? (
            <p className="text-sm text-[color:var(--brand-neutral)]">Aún no hay mensajes de contacto.</p>
          ) : (
            <>
              <p className="text-sm text-[color:var(--brand-neutral)]">
                {messages.length} mensajes · {messages.filter(m => !m.handled).length} pendientes
              </p>
              {messages.map((m) => (
                <div
                  key={m.id}
                  data-testid={`message-${m.id}`}
                  className={`bg-white border rounded-md p-4 space-y-2 ${m.handled ? "border-[color:var(--brand-border)] opacity-60" : "border-[color:var(--brand-terracotta)]/40"}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-serif text-lg text-[color:var(--brand-primary)]">{m.name}</p>
                      <a href={`mailto:${m.email}`} className="text-xs text-[color:var(--brand-terracotta)] hover:underline">{m.email}</a>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[color:var(--brand-neutral)]/60">{new Date(m.created_at).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded-sm ${m.handled ? "bg-[color:var(--brand-sage)]/20 text-[color:var(--brand-sage)]" : "bg-[color:var(--brand-terracotta)]/20 text-[color:var(--brand-terracotta)]"}`}>
                        {m.handled ? "Respondido" : "Pendiente"}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-[color:var(--brand-neutral)] whitespace-pre-line pt-1 border-t border-[color:var(--brand-border)]/50">{m.message}</p>
                  <div className="flex justify-end gap-2 pt-1">
                    <a href={`mailto:${m.email}?subject=Re:%20consulta%20a%20Mimes%20Gourmet&body=Hola%20${encodeURIComponent(m.name)}%2C%0A%0A`}
                       data-testid={`reply-${m.id}`}
                       className="text-xs px-3 py-1.5 rounded-sm border border-[color:var(--brand-border)] hover:bg-[color:var(--brand-bg-alt)] transition">
                      Responder
                    </a>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleHandled(m.id, !m.handled)}
                      data-testid={`toggle-${m.id}`}
                    >
                      {m.handled ? "Marcar pendiente" : "Marcar respondido"}
                    </Button>
                  </div>
                </div>
              ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="newsletter" className="mt-6">
          {subs.length === 0 ? <p className="text-sm text-[color:var(--brand-neutral)]">Sin suscriptores aún.</p> :
          <ul className="text-sm space-y-1">{subs.map((s) => <li key={s.email} className="py-1 border-b border-[color:var(--brand-border)]/50">{s.email}</li>)}</ul>}
        </TabsContent>

        <TabsContent value="emails" className="mt-6">
          {emails.length === 0 ? <p className="text-sm text-[color:var(--brand-neutral)]">Sin emails simulados.</p> :
          <div className="space-y-2">
            {emails.map((e, i) => (
              <div key={i} className="bg-white border border-[color:var(--brand-border)] rounded-md p-3 text-sm">
                <p><strong>Para:</strong> {e.to} · <span className="text-xs text-[color:var(--brand-neutral)]/60">SIMULADO</span></p>
                <p className="font-serif text-lg mt-1">{e.subject}</p>
                <p className="text-xs mt-1 text-[color:var(--brand-neutral)]">{e.body}</p>
              </div>
            ))}
          </div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
